import React, {useState, useEffect} from 'react';
import { Box, Card, Grid2 as Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useErrorBoundary } from 'react-error-boundary';

//import schedules
import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';
import ReceiptsScheduleTable from '../components/ReceiptsScheduleTable';
import MerchantProviderFeesScheduleTable from '../components/MerchantProviderFeesScheduleTable';
import { MerchantProviderFee } from '../../../types/reports/merchantProviderFee';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import ExpendituresScheduleTable from '../components/ExpendituresScheduleTable';
import BankReportContent from '../../../types/reports/bankReportContent';
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import changeLogCategory from '../../../types/reports/changeLogCategory';
import ChangeLogScheduleTable from '../components/ChangeLogScheduleTable';
import reportChangeLog from '../../../types/reports/reportChangeLog';
import ChangeLogHistory from '../components/ChangeLogHistory';

interface ReportProps {
   reportId: number;
   currentReport?: any;
}

const BankReport = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "50%", sm: "40%", md: "35%" };

    const [receiptsRows, setReceiptsRows] = useState<ReceiptItem[]>();
    const [expendituresRows, setExpendituresRows] = useState<ExpenditureItem[]>();
    const [merchantProviderFees, setMerchantProviderFees] = useState<MerchantProviderFee[]>();
    const [diffs, setDiffs] = useState<changeLogCategory[]>();
    const [diffsCount, setDiffsCount] = useState<number>();
    const [report, setReport] = useState<BankReportContent>();
    const [reportChangeLogs, setReportChangeLogs] = useState<reportChangeLog[]>();

    useEffect(() => {
            if(reportProps.currentReport){
                let report = reportProps.currentReport;
                 setScheduleValue('summary');
                 setReport(report);
                 setReceiptsRows(report.receipts);
                 setExpendituresRows(report.expenditures);
                 setMerchantProviderFees(report.merchantProviderFees);
                 setDiffs(report.diffs);
            }
            else{
                getFullReport();
            };
            getReportDiffs();
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get bank report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setReceiptsRows(report.receipts);
            setExpendituresRows(report.expenditures);
            setMerchantProviderFees(report.merchantProviderFees);
            setDiffs(report.diffs);
        });
        }catch(error){
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
    }


    const getReportDiffs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/diffs/${reportProps.reportId}`);
            if (!response.ok) {
                showBoundary(new Error('Failed to get report change log.'));
            }
            const diffs = await response.json();
            setReportChangeLogs(diffs);
        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

    const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
        setScheduleValue(newValue);
    };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="bank report schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.receipts?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Receipts [${report?.receipts?.length}]`} value="receipts" />
            }
            {
                (report?.expenditures?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Expenditures [${report?.expenditures?.length}]`} value="expenditures" />
            }
            {
                 (report?.merchantProviderFees?.length ?? 0) > 0 &&
                 <Tab className="report-tab-label" label={`Merchant Provider Fees [${report?.merchantProviderFees?.length}]`} value="fees" />
            }
            {
                 report?.diffs && report.isAmendment && report.isAmended &&
                 <Tab className="report-tab-label" label={`Amended Items`} value="changes"/>
            }
            {
                 !report?.isAmended && reportChangeLogs && reportChangeLogs.length > 0 &&
                 <Tab className="report-tab-label" label={`Change Log`} value="logs"/>
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filer:
                                </Typography>
                                <Typography component={Link} underline='hover' target="_blank" color="primary" href={`/filers?q=${report?.cpfId}`} variant={'body1'} fontWeight={550} align='left'>
                                     {report?.filerFullName} ({report?.cpfId})
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filing Bank:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth}>
                                     {report?.bankName}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'body1'} align='left' width={fieldWidth}>
                                     {report?.dateFiled}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                    Reporting Period:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} color="primary" align='left' width={fieldWidth}>
                                     {report?.reportingPeriod}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.previousReportId}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report?.isAmended && 
                    <ReportAmendedReasonBlock nextReportId={report?.nextReportId}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Beginning Balance:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.startBalance}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Receipts this period:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.creditTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Subtotal:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.subtotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Expenditures this period:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.expenditureTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Checking Ending Balance:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.endBalance}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Savings Balance as of {report?.endDate}:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.savingsBalance}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'} fontWeight={550}>
                                    Cash On Hand as of {report?.endDate}:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} align='right'>
                                     {report?.cashOnHand}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="receipts">
            <ReceiptsScheduleTable tableRows={receiptsRows}></ReceiptsScheduleTable>
            <Box padding={{ xs: 2, sm: 3, md: "10px" }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Itemized Receipts:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.filerReportedDepositReceiptItemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'} width={fieldWidth}>
                        Total Unitemized Receipts:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.filerReportedDepositReceiptUnitemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'} width={fieldWidth}>
                        Total Receipts:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550}>
                        {report?.filerReportedDepositReceiptTotal}
                    </Typography>
                </Stack>
                {
                    (report?.merchantProviderFees?.length ?? 0) > 0 &&
                    <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                        <Typography variant={'body1'}>
                            Merchant Provider Fee Total:
                        </Typography>
                        <Typography variant={'body1'} fontWeight={550} align='right'>
                            ({report?.merchantProviderFeeTotal})
                        </Typography>
                    </Stack>
                }
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Filer-Reported Net Receipt Total::
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.filerReportedDepositReceiptNetTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Bank-Reported Receipt Total:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.creditTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="expenditures">
            <ExpendituresScheduleTable tableRows={expendituresRows}></ExpendituresScheduleTable>
            <Box padding={{ xs: 2, sm: 3, md: "10px" }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Expenditures this Period:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.expenditureTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="fees">
            <MerchantProviderFeesScheduleTable merchantProviderFees={merchantProviderFees} total={report?.merchantProviderFeeTotal}></MerchantProviderFeesScheduleTable>
        </TabPanel>
        <TabPanel value="changes">
            <ChangeLogScheduleTable changes={diffs}></ChangeLogScheduleTable>
        </TabPanel>
        <TabPanel value="logs">
            <ChangeLogHistory changes={reportChangeLogs}></ChangeLogHistory>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default BankReport;